import React, { useEffect, useState } from "react"
import ReactDatePicker from "react-datepicker"
import { useIntl } from "react-intl"
import { presetsRanges } from "./CalendarPresets/Presets"
import {
	MultipleRangeCalendarProps,
	InternalDate,
	RangeCalendar,
	DefinedRangeType,
	RangeDate,
} from "./MultiRangeCalendar.domain"
import {
	addCustomRangePreset,
	getBorderRangeClass,
	getCircularPos,
	getIndexCalendarInDateReduce,
	setDateCalendarRange,
} from "./MultiRangeCalendar.utils"
import {
	END_RANGE_DATE_POSITION,
	START_RANGE_DATE_POSITION,
	NOT_INDEX_CALENDAR_FOUND,
	CUSTOM_RANGES_ID,
	BLANK_SPACE,
} from "./constants"
import "./MultiRangeCalendar.styles.scss"
import { getPresetDefault } from "./CalendarPresets/utils/utils"
import { EMPTY_STRING } from "constants/constants"
import CalendarCustomHeader from "./CalendarCustomHeader"
import CalendarInput from "./CalendarInput"
import CalendarBody from "./CalendarBody"
import {
	DATE_FORMAT,
	DEFAULT_DATE_FORMAT,
	formatDate,
} from "../../../helpers/temporal"
import { useSetState } from "./hooks/useSetState"
import "react-datepicker/dist/react-datepicker.css"

const MultiRangeCalendar = (props: MultipleRangeCalendarProps) => {
	const {
		ranges = [],
		definedRanges: initialDefinedRanges = presetsRanges,
		defaultTitle,
		selectedPreset: selectedPresetId = "",
		showPresets = false,
		onChange = () => undefined,
		onSubmit = () => undefined,
		onCancelSubmit = () => undefined,
		footerMenu,
		...reactDatePickerProps
	} = props

	formatDate(new Date(), DEFAULT_DATE_FORMAT)

	const rangeLength = ranges.length
	const [startDate, setStartDate] = useState<InternalDate>(null)
	const [endDate, setEndDate] = useState<InternalDate>(null)
	const [calendarRangesSelected, setCalendarRangesSelected] = useState(ranges)
	const [focusedCalendarRange, setFocusedCalendarRange] = useState([0, 0])
	const [definedRanges, setDefinedRanges] = useSetState<DefinedRangeType[]>(
		() => addCustomRangePreset(initialDefinedRanges)
	)
	const [definedRangeSelected] = definedRanges.filter(
		({ isSelected }) => isSelected
	)
	const [selectedDate, setSelectedDate] = useSetState<InternalDate>(new Date())
	const [showClearButtons, setShowClearButtons] = useState(false)

	const getDefaultTitle = () => {
		if (definedRangeSelected?.label) {
			return definedRangeSelected.label
		}
		return defaultTitle || BLANK_SPACE
	}
	const intl = useIntl()
	const label = intl.formatMessage({
		id: getDefaultTitle(),
	})
	const [
		definedRangesSelected,
		setDefinedRangesSelected,
		getDefinedRangesSelected,
	] = useSetState(definedRanges)

	const [isOpen, setIsOpen] = useState(false)

	const selectDefinedIdRange = (id: string) => {
		setDefinedRangesSelected((current) =>
			current?.map((definedRange) => ({
				...definedRange,
				isSelected: definedRange.id === id,
			}))
		)
	}

	const getDefinedRangeSelectedId = () => {
		return definedRangesSelected.filter(({ isSelected }) => isSelected)[0].id
	}

	useEffect(() => {
		handleSelectedPresetByDefault()
	}, [])

	const handleSelectedPresetByDefault = async () => {
		if (selectedPresetId === EMPTY_STRING) {
			return
		}

		let defaultRange: RangeCalendar[]

		if (selectedPresetId === CUSTOM_RANGES_ID) {
			defaultRange = ranges
		} else {
			defaultRange = getPresetDefault(
				definedRanges,
				selectedPresetId
			) as RangeCalendar[]
		}

		selectDefinedIdRange(selectedPresetId)
		setCalendarRangesSelected(defaultRange)
		onChange(defaultRange, -1, undefined)
		setDefinedRanges(await getDefinedRangesSelected())
		onSubmit(defaultRange, selectedPresetId, undefined)
	}

	const setCalendarSelector = (
		startDate: InternalDate,
		endDate: InternalDate
	) => {
		setStartDate(startDate)
		setEndDate(endDate)
	}

	const renderDayContents = (day: number, date: Date) => {
		const [
			rangesCalendarIndex = NOT_INDEX_CALENDAR_FOUND,
		]: number[] = ranges
			.reduce(getIndexCalendarInDateReduce(date), [])
			.slice(-1)

		if (rangesCalendarIndex === NOT_INDEX_CALENDAR_FOUND) {
			return <div aria-label={`${formatDate(date, DATE_FORMAT)}`}>{day}</div>
		}

		const { className, stylesParent, stylesChild } = getBorderRangeClass(
			date,
			ranges[rangesCalendarIndex]
		)

		return (
			<div style={stylesParent}>
				<div
					aria-label={`${formatDate(date, DATE_FORMAT)}`}
					data-testid={`selected-range-${rangesCalendarIndex}`}
					style={stylesChild}
					className={className}
				>
					{day}
				</div>
			</div>
		)
	}

	const handleOnChange = (
		dates: RangeDate,
		event: React.SyntheticEvent<any> | undefined
	) => {
		const [startDate, endDate] = dates
		const [focusedCalendarIndex] = focusedCalendarRange

		if (endDate !== null) {
			setCalendarSelector(null, null)
			const newIndex = getCircularPos(rangeLength, focusedCalendarIndex + 1)
			setFocusedCalendarRange([newIndex, START_RANGE_DATE_POSITION])
		} else {
			setCalendarSelector(startDate, endDate)
			setFocusedCalendarRange([focusedCalendarIndex, END_RANGE_DATE_POSITION])
		}

		selectDefinedIdRange(CUSTOM_RANGES_ID)
		setShowClearButtons(true)

		onChange(
			setDateCalendarRange(ranges, dates, focusedCalendarIndex),
			focusedCalendarIndex,
			event
		)
	}

	const handleOnFocusHeader = (newFocusedCalendarRange: number[]) => {
		const [focusedCalendarIndex, dateIndex] = newFocusedCalendarRange
		const { startDate, endDate } = ranges[focusedCalendarIndex]

		if (dateIndex === END_RANGE_DATE_POSITION) {
			setFocusedCalendarRange([focusedCalendarIndex, END_RANGE_DATE_POSITION])
			setCalendarSelector(startDate, null)
			setDateCalendarRange(ranges, [startDate, null], focusedCalendarIndex)
			setSelectedDate(endDate)
		} else {
			setFocusedCalendarRange([focusedCalendarIndex, START_RANGE_DATE_POSITION])
			setCalendarSelector(null, null)
			setDateCalendarRange(ranges, [null, null], focusedCalendarIndex)
			setSelectedDate(startDate)
		}
	}

	const handleOnChangeDefinedRanges = (
		calendarRanges: RangeCalendar[],
		definedId: string
	) => {
		handleShowClearButtons(definedId)
		onChange(calendarRanges, -1, undefined)
		selectDefinedIdRange(definedId)
	}

	const handleOnSubmit = (event: React.SyntheticEvent<any> | undefined) => {
		setCalendarRangesSelected(ranges)
		setDefinedRanges(definedRangesSelected)
		onSubmit(ranges, getDefinedRangeSelectedId(), event)
		setIsOpen(false)
	}

	const handleOnCancelSubmit = (
		event: React.SyntheticEvent<any> | undefined
	) => {
		setDefinedRangesSelected(definedRanges)
		onChange(calendarRangesSelected, -1, event)
		onCancelSubmit(event)
		setIsOpen(false)
		handleShowClearButtons(definedRangeSelected?.id)
	}

	const handleShowClearButtons = (definedRangeId: string) => {
		if (definedRangeId === CUSTOM_RANGES_ID) {
			setShowClearButtons(true)
		} else {
			setShowClearButtons(false)
		}
	}

	const handleOnClearPeriod = (index: number) => {
		const [focusedCalendarIndex] = focusedCalendarRange

		setFocusedCalendarRange([index, START_RANGE_DATE_POSITION])

		onChange(
			setDateCalendarRange(ranges, [null, null], index),
			focusedCalendarIndex,
			undefined
		)
	}

	return (
		<ReactDatePicker
			open={isOpen}
			onCalendarOpen={() => setIsOpen(true)}
			onCalendarClose={() => setIsOpen(false)}
			onClickOutside={() => {
				setIsOpen(false)
				onChange(calendarRangesSelected, -1, undefined)
				setDefinedRangesSelected(definedRanges)
				handleShowClearButtons(definedRangeSelected?.id)
			}}
			onInputClick={() =>
				!reactDatePickerProps.disabled && setIsOpen((c) => !c)
			}
			onChange={handleOnChange}
			selectsRange
			disabledKeyboardNavigation
			renderDayContents={renderDayContents}
			startDate={startDate}
			endDate={endDate}
			openToDate={selectedDate ?? undefined}
			shouldCloseOnSelect={false}
			customInput={
				<CalendarInput
					text={label}
					isOpen={isOpen}
					calendarRangesSelected={calendarRangesSelected}
				/>
			}
			calendarContainer={({ children }) => (
				<CalendarBody
					ranges={ranges}
					definedRanges={definedRangesSelected}
					showPresets={showPresets}
					focusedCalendarRange={focusedCalendarRange}
					onFocusHeader={handleOnFocusHeader}
					onChangePresets={handleOnChangeDefinedRanges}
					onSubmit={handleOnSubmit}
					onCancelSubmit={handleOnCancelSubmit}
					footerMenu={footerMenu}
					showCleanButtons={showClearButtons}
					onClearPeriod={handleOnClearPeriod}
				>
					{children}
				</CalendarBody>
			)}
			renderCustomHeader={(props) => (
				<CalendarCustomHeader {...reactDatePickerProps} {...props} />
			)}
			popperModifiers={[
				{
					name: "offset",
					options: {
						offset: [13, -20],
					},
				},
			]}
			{...reactDatePickerProps}
		/>
	)
}

export default MultiRangeCalendar
